import type { LocaleResources } from 'locales';
import React, { useRef } from 'react';
import { CloseIcon } from 'src/components/shared/elements/icons/CloseIcon';
import { HistoryIcon } from 'src/components/shared/elements/icons/HistoryIcon';
import { SearchIcon } from 'src/components/shared/elements/icons/SearchIcon';
import styles from './index.module.scss';

type Props = {
  searchKeyword: string;
  autoCompletionKeywords: string[];
  searchHistories: string[];
  popularKeywords: string[];
  onSearchChange: (value: string) => void;
  onSubmit: (value: string) => void;
  onHistoryClick: (history: string) => void;
  onDeleteHistory: (history: string) => void;
  onAutoCompletionKeywordClick: (selectedQuery: string, index: number) => void;
  onPopularKeywordClick: (keyword: string) => void;
  onClose: () => void;
  inputRef: React.RefObject<HTMLInputElement>;
  localeResources: LocaleResources;
};

export const Presenter: React.FC<Props> = ({
  searchKeyword,
  autoCompletionKeywords,
  searchHistories,
  popularKeywords,
  onSearchChange,
  onSubmit,
  onHistoryClick,
  onDeleteHistory,
  onAutoCompletionKeywordClick,
  onPopularKeywordClick,
  onClose,
  inputRef,
  localeResources
}) => {
  const isComposingRef = useRef(false);
  return (
    <fieldset className={styles.wrapper} onClick={onClose}>
      <legend className={styles.legend}>SearchOverlay</legend>
      <div className={styles.container}>
        <div className={styles.searchBarContainer}>
          <input
            type="text"
            className={styles.searchInput}
            ref={inputRef}
            title={localeResources.search.brandOrProductOrProduct}
            value={searchKeyword}
            autoFocus
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={(e) => {
              if ((e.code === 'Enter' || e.key === 'Enter') && !isComposingRef.current) onSubmit(searchKeyword);
            }}
            onCompositionStart={() => (isComposingRef.current = true)}
            onCompositionEnd={() => (isComposingRef.current = false)}
          />
          <div onClick={onClose} className={styles.closeIconContainer} role="button" aria-label="closeOverlay">
            <CloseIcon width={15} height={15} />
          </div>
        </div>

        {!!popularKeywords.length && (
          <div className={styles.popularKeywordsContainer}>
            <h2 className={styles.popularKeywordsTitle}>{localeResources.search.popularKeywords}</h2>
            <ul className={styles.popularKeywords}>
              {popularKeywords.map((keyword) => (
                <li key={keyword} className={styles.popularKeyword}>
                  <button onClick={() => onPopularKeywordClick(keyword)}>#{keyword}</button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {!!autoCompletionKeywords.length && (
          <div className={styles.autoCompletionKeywordsContainer}>
            <h2 className={styles.autoCompletionKeywordsTitle}>{localeResources.search.recommendedKeywords}</h2>
            <div className={styles.divider}></div>
            <ul className={styles.autoCompletionKeywords}>
              {autoCompletionKeywords.map((keyword, index) => (
                <li className={styles.autoCompletionKeyword} key={index}>
                  <button
                    className={styles.autoCompletionKeywordButton}
                    onClick={() => onAutoCompletionKeywordClick(keyword, index)}
                  >
                    <SearchIcon width={22} height={22} />
                    <p className={styles.autoCompletionKeywordTextWrap}>
                      {keyword}
                      <span className={styles.autoCompletionKeywordText}>{localeResources.search.keyword}</span>
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className={styles.searchHistoryContainer}>
          <div className={styles.searchHistoryTitleContainer}>
            <h2 className={styles.searchHistoryTitle}>{localeResources.search.history}</h2>
          </div>
          <div className={styles.divider}></div>
          <ul className={styles.searchHistoryListContainer}>
            {searchHistories.map((history) => (
              <li key={history} className={styles.searchHistoryListItemContainer}>
                <div className={styles.historyIconContainer}>
                  <HistoryIcon />
                </div>
                <p className={styles.searchHistory} onClick={() => onHistoryClick(history)}>
                  {history}
                </p>
                <div
                  className={styles.searchHistoryCloseIconContainer}
                  onClick={() => onDeleteHistory(history)}
                  role="button"
                  aria-label="closeSearchHistory"
                >
                  <CloseIcon width={15} height={15} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </fieldset>
  );
};
